/* eslint-disable jsx-a11y/no-noninteractive-element-interactions */
/* eslint-disable jsx-a11y/no-autofocus */
/* eslint-disable jsx-a11y/click-events-have-key-events */
import { UsersIcon } from '@heroicons/react/16/solid';
import { BookOpenIcon,ExternalLink } from 'lucide-react';
import * as React from 'react';
import { Link, useParams } from 'react-router-dom';

import { CompanionLabel } from '@/components/common/CompanionLabel';
import { Badge } from '@/components/ui/badge';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import { useGetTaskStatusesQuery } from '@/services/api';
import { useGetTaskCompanionTasksQuery } from '@/services/api/tasksService';
import { Task, TaskStatus } from '@/services/types/client-intake-types';
import { CanonicalTaskStatus } from '@/services/types/task-types';
import { filterTaskStatusesByGroup } from '@/utils/taskStatusUtils';

import { TaskStatusBadge } from '../TaskStatusBadge';
import { useDebounce } from './hooks';
import { LinkedEntityBadge } from './LinkedEntityBadge';

/** Banner showing other companion cases with links (deduplicated by matter) */
const CompanionBadge: React.FC<{
  matterId: string;
  taskId: string;
  companionCount: number;
  isCurrentPrimary: boolean;
}> = ({ matterId, taskId, companionCount, isCurrentPrimary }) => {
  const { data } = useGetTaskCompanionTasksQuery({ matterId, taskId });

  const uniqueMatters = React.useMemo(() => {
    const companions = data?.companion_tasks ?? [];
    const seen = new Set<string>();
    return companions
      .filter((c) => c.matter_id !== matterId)
      .filter((c) => {
        if (seen.has(c.matter_id)) return false;
        seen.add(c.matter_id);
        return true;
      })
      .sort((a, b) => a.matter_name.localeCompare(b.matter_name));
  }, [data?.companion_tasks, matterId]);

  const label = `${companionCount - 1} companion${companionCount - 1 !== 1 ? 's' : ''}`;

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Badge
          variant="secondary"
          className="text-[9px] font-medium bg-purple-50 text-purple-700 border-purple-200 cursor-default gap-1"
        >
          <UsersIcon className="h-2.5 w-2.5 shrink-0" />
          {label}
        </Badge>
      </TooltipTrigger>
      <TooltipContent side="bottom" className="max-w-[240px]">
        <div className="space-y-1">
          <div className="text-xs font-semibold">Companion Cases</div>
          {uniqueMatters.length > 0 ? (
            uniqueMatters.map((companion) => (
              <div key={companion.matter_id} className="text-xs">
                <Link
                  to={`/matters/${companion.matter_id}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:underline"
                >
                  {companion.matter_name}
                  {!isCurrentPrimary && companion.is_primary && ' (Primary)'}
                </Link>
              </div>
            ))
          ) : (
            <div className="text-xs text-slate-400">Loading...</div>
          )}
        </div>
      </TooltipContent>
    </Tooltip>
  );
};

interface TaskHeaderProps {
  task: Task;
  onTitleChange: (title: string) => void;
  onStatusChange: (status: TaskStatus) => void;
  /** When true, hides status badge and Mark Complete button (used when these controls are in the panel header) */
  hideStatusControls?: boolean;
  /** When true, suppresses the linked treatment-request badge to reduce duplicate provider chrome */
  hideTreatmentRequestBadge?: boolean;
  /** Slot rendered between the title and the linked entity badges (e.g. metadata chips) */
  metadataSlot?: React.ReactNode;
  /** Slot rendered right after metadata chips, before linked entities and action button */
  instructionSlot?: React.ReactNode;
  /** Slot rendered between metadata and instructions (e.g. reassignment notices) */
  noticeSlot?: React.ReactNode;
  /** Slot rendered after the linked entity badges (e.g. action button) */
  actionSlot?: React.ReactNode;
}

export const TaskHeader: React.FC<TaskHeaderProps> = ({
  task,
  onTitleChange,
  onStatusChange,
  hideStatusControls = false,
  hideTreatmentRequestBadge = false,
  metadataSlot,
  instructionSlot,
  noticeSlot,
  actionSlot,
}) => {
  const { data: statuses } = useGetTaskStatusesQuery();
  const [editingTitle, setEditingTitle] = React.useState(false);
  const [titleValue, setTitleValue] = React.useState(task.name);
  const [hasUserEdited, setHasUserEdited] = React.useState(false);
  const { matter_id } = useParams();

  const debouncedTitle = useDebounce(titleValue, 500);

  // Update local title when task name changes (but not when we're editing)
  React.useEffect(() => {
    if (!editingTitle && !hasUserEdited) {
      setTitleValue(task.name);
    }
  }, [task.name, editingTitle, hasUserEdited]);

  // Only trigger update when user has made changes and debounced value is different
  React.useEffect(() => {
    if (hasUserEdited && debouncedTitle !== task.name) {
      onTitleChange(debouncedTitle);
      setHasUserEdited(false);
    }
  }, [debouncedTitle, task.name, onTitleChange, hasUserEdited]);

  const handleTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setTitleValue(e.target.value);
    setHasUserEdited(true);
  };

  const handleTitleBlur = () => {
    setEditingTitle(false);
    if (titleValue !== task.name) {
      onTitleChange(titleValue);
    }
  };

  const titleInputRef = React.useRef<HTMLInputElement>(null);

  const handleMarkAsDone = () => {
    // Filter statuses by task group first (same logic as TaskStatusBadge)
    const filteredStatuses = filterTaskStatusesByGroup(statuses || [], task);

    // Find status named "Completed" or "Complete", fallback to any COMPLETED canonical status
    const completedStatus =
      filteredStatuses.find(
        (s) =>
          s.canonical_status === CanonicalTaskStatus.COMPLETED &&
          (s.name.toLowerCase() === 'completed' ||
            s.name.toLowerCase() === 'complete'),
      ) ||
      filteredStatuses.find(
        (s) => s.canonical_status === CanonicalTaskStatus.COMPLETED,
      );
    if (completedStatus) {
      onStatusChange(completedStatus);
    }
  };

  const isCompleted =
    task.status.canonical_status === CanonicalTaskStatus.COMPLETED;
  const showLinkedEntityRow =
    !hideTreatmentRequestBadge || (task.companion_count ?? 0) > 1;

  const displayTitle = React.useMemo(() => {
    if (!hideTreatmentRequestBadge || !task.treatment_record_request?.id) {
      return task.name;
    }

    return task.name.replace(/\s+from\s+.+$/i, '');
  }, [hideTreatmentRequestBadge, task.name, task.treatment_record_request?.id]);

  return (
    <div className="space-y-2" style={{ WebkitTapHighlightColor: 'transparent' }}>
      {/* Matter link - only show if not already on matter page */}
      {!matter_id && (
        <div className="flex items-center gap-2">
          <Link
            to={`/matters/${task.matter}/?task_id=${task.id}`}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-xs text-zinc-600 dark:text-zinc-400 hover:text-blue-600 dark:hover:text-blue-400 font-medium"
          >
            {task.matter_name}
            <ExternalLink className="h-3 w-3" />
          </Link>
          <CompanionLabel
            memberCount={task.companion_count ?? 0}
            isPrimary={task.is_companion_primary ?? false}
            size="sm"
          />
        </div>
      )}

      {/* Title and Status - More compact */}
      <div className="flex items-start justify-start flex-col gap-1 text-xs font-bold w-full">
        {editingTitle ? (
          <input
            ref={titleInputRef}
            type="text"
            value={titleValue}
            onChange={handleTitleChange}
            onBlur={handleTitleBlur}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                handleTitleBlur();
              }
              if (e.key === 'Escape') {
                setTitleValue(task.name);
                setEditingTitle(false);
              }
            }}
            className=" text-slate-900 w-full flex-1 border-0 ring-1 ring-slate-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
            autoFocus
          />
        ) : (
          <div className="flex-1 flex items-center gap-2">
            <Tooltip>
              <TooltipTrigger asChild>
                <h1
                  className={cn(
                    'text-slate-900 cursor-text text-wrap',
                    'hover:bg-slate-50 rounded px-2 py-1 -ml-2 transition-colors focus:outline-none',
                  )}
                  onClick={() => {
                    setEditingTitle(true);
                    setTimeout(() => {
                      titleInputRef.current?.focus();
                    }, 0);
                  }}
                >
                  {displayTitle}
                </h1>
              </TooltipTrigger>
              <TooltipContent side="left" className="text-xs">
                {task.name}
              </TooltipContent>
            </Tooltip>
            {task.playbook_url && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <a
                    href={task.playbook_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="shrink-0 text-slate-600 hover:text-blue-600 transition-colors -ml-2 "
                  >
                    <BookOpenIcon className="h-3.5 w-3.5" />
                  </a>
                </TooltipTrigger>
                <TooltipContent side="right" className="text-xs">
                  View task playbook
                </TooltipContent>
              </Tooltip>
            )}
          </div>
        )}
          {instructionSlot && (
          <div className="w-full">{instructionSlot}</div>
        )}
        {metadataSlot && (
          <div className="w-full">{metadataSlot}</div>
        )}
        {noticeSlot && (
          <div className="w-full">{noticeSlot}</div>
        )}

        {showLinkedEntityRow && (
          <div className="flex justify-start items-center gap-1.5 w-full flex-wrap mt-1">
            {!hideTreatmentRequestBadge && (
              <LinkedEntityBadge
                coverage={task.coverage}
                medicalNote={task.medical_note}
                policeReport={task.police_report}
                treatmentRequest={task.treatment_record_request}
                matterId={task.matter}
              />
            )}
            {(task.companion_count ?? 0) > 1 && (
              <CompanionBadge
                matterId={task.matter}
                taskId={task.id}
                companionCount={task.companion_count ?? 0}
                isCurrentPrimary={task.is_companion_primary ?? false}
              />
            )}
          </div>
        )}
        {actionSlot && (
          <div className="w-full">{actionSlot}</div>
        )}
        {!hideStatusControls && (
          <div className="flex items-center gap-1">
            <TaskStatusBadge
              status={task.status}
              onStatusChange={onStatusChange}
              task={task}
            />
            {!isCompleted && (
              <button
                type="button"
                onClick={handleMarkAsDone}
                className="p-1 text-slate-800 transition-colors text-[9px] hover:bg-green-200/50 hover:text-green-900 rounded border"
              >
                Mark Complete
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
